/**
 * AddressAutocomplete — v2.5.126 (Phase 11.2)
 * ─────────────────────────────────────────────────────────────────
 * Drop-in replacement for an address `<input>` that wires up
 * Google Places Autocomplete when the integration is configured.
 *
 * When NOT configured (admin hasn't set the API key, or the
 * loader fails), this component renders an ordinary text input
 * — same look, same behavior. Graceful degrade is the whole point.
 *
 * Props:
 *   value             string — the current address text
 *   onChange(value)   called on every keystroke
 *   onPlaceSelected(p) optional — called when the user picks a
 *                      suggestion. p has:
 *                        { formatted_address, lat, lng,
 *                          components: { street, city, state,
 *                                        country, postal_code } }
 *                      Use this if a consuming page wants to
 *                      auto-fill OTHER fields (city, postal).
 *                      If only `onChange` is provided, it gets
 *                      called with the formatted_address.
 *   placeholder       string
 *   style             merged into the input's inline style
 *   className         passed through
 *
 * Implementation notes:
 *   • We use the legacy `google.maps.places.Autocomplete` (not the
 *     newer `PlaceAutocompleteElement` web component) because it
 *     plays nicer with React-controlled inputs. Both are
 *     supported by Google long-term as of 2026.
 *   • The Autocomplete instance binds its UI to the input
 *     element. Cleanup on unmount: remove the listener but keep
 *     the gmaps lib loaded (other components may want it).
 *   • Country bias: defaults to {US, CA} since that covers the
 *     primary user base. Consumers can override via prop later
 *     if we add international leagues.
 */

import { useEffect, useRef, useState } from 'react';
import { ensureMapsLoaded } from '../services/mapsLoader.js';

const DEFAULT_COUNTRY_BIAS = ['us', 'ca'];

export default function AddressAutocomplete({
  value = '',
  onChange,
  onPlaceSelected,
  placeholder = 'Address',
  style,
  className,
  countries = DEFAULT_COUNTRY_BIAS,
  disabled,
  autoFocus,
}) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  // v2.5.126 audit fix — keep the listener handle in a ref so the
  // outer useEffect cleanup can remove it. Previously the cleanup
  // function returned from inside the .then() was discarded by
  // React (only the outer effect's return value is picked up).
  const listenerRef = useRef(null);
  // Stable refs to the callbacks. The place_changed listener is
  // attached ONCE on mount and lives for the component's lifetime;
  // without refs, it would close over the first render's onChange /
  // onPlaceSelected and ignore later updates.
  const onChangeRef = useRef(onChange);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onPlaceSelectedRef.current = onPlaceSelected; }, [onPlaceSelected]);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    ensureMapsLoaded().then((maps) => {
      if (cancelled || !maps || !inputRef.current) return;

      // Build the Autocomplete instance. `fields` keeps the
      // response small (the more fields requested, the higher
      // the cost per session, and we only need address + geometry).
      const ac = new maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: countries },
        fields: ['formatted_address', 'geometry', 'address_components', 'name'],
      });
      autocompleteRef.current = ac;
      setEnabled(true);

      // Place-selected handler. Two paths: a structured callback
      // (onPlaceSelected) for consumers that want geometry +
      // components, and the simpler onChange (for consumers that
      // just want the formatted address).
      listenerRef.current = ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place) return;

        const formatted = place.formatted_address || place.name || '';
        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();

        // Normalize the address_components array into a small
        // dict so consumers don't have to repeat the same loop.
        const components = {};
        for (const c of (place.address_components || [])) {
          const types = c.types || [];
          if (types.includes('street_number')) components.street_number = c.long_name;
          if (types.includes('route')) components.street = c.long_name;
          if (types.includes('locality')) components.city = c.long_name;
          if (types.includes('administrative_area_level_1')) {
            components.state = c.short_name;
            components.state_long = c.long_name;
          }
          if (types.includes('country')) {
            components.country = c.short_name;
            components.country_long = c.long_name;
          }
          if (types.includes('postal_code')) components.postal_code = c.long_name;
        }

        if (onPlaceSelectedRef.current) {
          onPlaceSelectedRef.current({
            formatted_address: formatted,
            lat, lng,
            components,
            place_id: place.place_id,
          });
        }
        // Always update the bound value so React sees the change.
        // (Without this, the input would visually show the picked
        // address but the parent's state wouldn't reflect it.)
        if (onChangeRef.current) onChangeRef.current(formatted);
      });
    }).catch((e) => {
      console.warn('[AddressAutocomplete] init failed:', e?.message);
    });

    return () => {
      cancelled = true;
      // Properly remove the place_changed listener so a parent
      // that mounts/unmounts repeatedly doesn't accumulate stale
      // callbacks against an Autocomplete that's still attached
      // to a now-detached input.
      if (listenerRef.current?.remove) {
        listenerRef.current.remove();
        listenerRef.current = null;
      }
    };
    // We intentionally exclude `countries` from deps — changing
    // it post-mount would require destroying + recreating the
    // Autocomplete, which we don't expect in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={enabled ? placeholder : `${placeholder} (autocomplete not configured)`}
      disabled={disabled}
      autoFocus={autoFocus}
      autoComplete="off"
      className={className}
      // The `.pac-container` is the dropdown Google injects.
      // It floats above everything via fixed positioning, so we
      // don't need to do any z-index gymnastics here.
      style={style}
    />
  );
}
